import { useEffect, useState } from "react";

// Constants and Utils
import { FormDataTargets } from "../../../constants/GeneralConstants";
import {
  formatText,
  toLocaleString,
} from "../../../common-utils/string-utils/StringUtils";

// Components
import TableHeaders from "../../../components/table/TableHeaders";
import { MediaImage } from "../../../components/campaign-media-table-row/MediaRow";
import SearchInput from "../../../components/search-input/SearchInput";

// table headers
const tableHeaders = [
  {
    title: {
      displayName: "Site Name",
      className: "col-3",
    },
  },
  {
    title: {
      displayName: "Device ID",
      className: "col-1 text-center",
    },
  },
  {
    title: {
      displayName: "Latitude Longitude",
      className: "col-2 text-center",
    },
  },
  {
    title: {
      displayName: "Static/Digital",
      className: "col-1 text-center",
    },
  },
  {
    title: {
      displayName: "Road Stretch",
      className: "col-3 text-center",
    },
  },
];

function InventoryUserMediaDetailsTableRow({ media = {} }) {
  const { title, sourceId, latitude, longitude, isDigital, roadStretchName } =
    media || {};

  const staticDigitalText = isDigital ? "Digital" : "Static";

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <MediaImage media={media} />
          <span className="pl-2">{title}</span>
        </div>
      </td>
      <td className="text-center">{sourceId}</td>
      <td className="text-center">
        {latitude},{longitude}
      </td>
      <td className="text-center">{staticDigitalText}</td>
      <td className="text-center">
        <span title={roadStretchName}>{formatText(roadStretchName)}</span>
      </td>
    </tr>
  );
}

/**
 * Inventory User Medias Details Form
 */
export default function InventoryUserMediasDetailsForm({
  userAssociatedMediasMap = {},
  selectedUserId = "",
  setSelectedUserId = () => {},
  setOpenUserMediasModal = () => {},
}) {
  // State
  const [searchedMediaList, setSearchedMediaList] = useState([]);

  // user media list
  const userMediaList = userAssociatedMediasMap?.[selectedUserId] || [];
  const totalMediaCount = userMediaList.length;
  const formattedTotalMediaCount = toLocaleString(totalMediaCount);

  // initial state update
  useEffect(() => {
    setSearchedMediaList(userMediaList);
  }, []);

  // search media
  function handleSearchMedia(e) {
    const searchText = e.target.value?.toLowerCase() || "";

    // if no searchText, then update with initial list
    if (!searchText) {
      setSearchedMediaList(userMediaList);
      return;
    }

    // filter by sourceId or title or stretchName
    const filteredMedias = userMediaList.filter((media) => {
      const { title, sourceId, roadStretchName } = media;
      const titleMatch = title?.toLowerCase().includes(searchText);
      const sourceIdMatch = sourceId?.toLowerCase().includes(searchText);
      const stretchMatch = roadStretchName?.toLowerCase().includes(searchText);
      return titleMatch || sourceIdMatch || stretchMatch;
    });
    setSearchedMediaList(filteredMedias);
  }

  // function
  function handleClose() {
    setSelectedUserId("");
    setOpenUserMediasModal(false);
  }

  return (
    <div
      className="modal fade"
      id={FormDataTargets.inventoryUserMediasDetailsForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          {/* Form Header */}
          <div className="modal-header bg-alt px-5">
            <h4 className="modal-title">{`User (${selectedUserId}) Medias Details`}</h4>
          </div>

          {/* Form body */}
          <div className="modal-body px-5">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Total Media: {formattedTotalMediaCount}</h5>
              <SearchInput
                placeholder="Search Media Site"
                searchButton={false}
                onTextChange={handleSearchMedia}
              />
            </div>

            {/* table */}
            <div className="table-responsive max-h-400 mt-2">
              <table className="table">
                <TableHeaders
                  tableHeaders={tableHeaders}
                  headerClass={"thead"}
                />
                <tbody>
                  {searchedMediaList.map((media) => (
                    <InventoryUserMediaDetailsTableRow
                      key={media.id}
                      media={media}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Form Footer */}
          <div className="modal-footer px-5">
            <button
              type="button"
              className="btn btn-outline-primary btn-action mt-2 rounded-lg shadow-none"
              data-dismiss="modal"
              onClick={handleClose}
            >
              {"Close"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
